<script setup lang="ts">
import AppRating from '@/features/AppRating/index';
import {
  COMPANY_WORKS_AT_LABEL,
  COMPANY_FEEDBACKS_LABEL,
  COMPANY_CALLBACK_BUTTON_TEXT,
  COMPANY_LOGO_ALT,
} from '@/pages/lawyer/lib/contactSectionConstants';
import type {
  ICompanyProfileProps,
  ICompanyProfileEmits,
} from '@/pages/lawyer/model/types/companyProfile';

withDefaults(defineProps<ICompanyProfileProps>(), {
  urlLogo: '',
  feedbackRating: 0,
  feedbackCount: 0,
});

const emit = defineEmits<ICompanyProfileEmits>();

const isMobile: boolean = useIsMobile();
</script>

<template>
  <div :class="['company-profile', { _mobile: isMobile }]" data-testid="contact-company">
    <div class="company-profile__info">
      <div class="company-profile__image-wrapper">
        <img
          class="company-profile__image"
          data-testid="contact-company-logo"
          :src="urlLogo"
          :alt="COMPANY_LOGO_ALT"
        />
      </div>
      <div class="company-profile__content">
        <span class="company-profile__subtitle">{{ COMPANY_WORKS_AT_LABEL }}</span>
        <NuxtLink
          class="company-profile__link"
          :to="url"
          data-testid="contact-company-link"
        >
          <span class="company-profile__name" data-testid="contact-company-name">
            {{ name }}
          </span>
        </NuxtLink>
        <div class="company-profile__rating">
          <AppRating disabled :model-value="feedbackRating" />
        </div>
        <div class="company-profile__feedbacks">
          <NuxtLink
            class="company-profile__feedback-button"
            :to="url"
            data-testid="contact-company-feedbacks"
          >
            {{ COMPANY_FEEDBACKS_LABEL }} <strong>({{ feedbackCount }})</strong>
          </NuxtLink>
        </div>
      </div>
    </div>
    <div class="company-profile__actions">
      <div v-if="!isMobile" class="company-profile__action">
        <AppButton
          class="company-profile__button"
          data-testid="contact-company-callback"
          :button-text="COMPANY_CALLBACK_BUTTON_TEXT"
          sup-text="0&#8381;"
          left-icon-name="Phone"
          left-icon-width="15"
          left-icon-height="15"
          is-hover-pulse-effect
          @click="emit('callback')"
        />
      </div>
    </div>
  </div>
</template>

<style lang="less">
.company-profile {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;

  &__info {
    display: flex;
  }

  &__subtitle {
    font-weight: 500;
    font-size: @size-x3;
    line-height: @size-x4;
    color: @color-dove-gray;
  }

  &__name {
    font-weight: 600;
    font-size: 14px;
    line-height: 19px;
    margin-bottom: @size-x4;
  }

  &__link {
    .color-change-hover();

    display: inline-block;
    margin-bottom: @main-size;
  }

  &__image-wrapper {
    width: 88px;
    height: 88px;
  }

  &__image {
    width: 100%;
    height: 100%;
    border-radius: @main-size;
  }

  &__content {
    display: flex;
    flex-direction: column;
    margin-left: 10px;
    min-width: 100px;
  }

  &__rating {
    margin-bottom: @main-size;
    max-width: 120px;
  }

  &__feedbacks {
    font-size: 14px;
    line-height: 21px;
    font-weight: 500;
  }

  &__feedback-button {
    .color-change-hover();

    font-weight: 500;
  }

  &__actions {
    gap: 10px;
    margin-left: auto;
  }

  &__action:not(:last-child) {
    margin-bottom: @size-x3;
  }
}
</style>
